import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { X, Send, Instagram, Linkedin, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "@/lib/utils";
import BrandName from "@/components/BrandName";

// Custom WhatsApp Icon
const WhatsAppIcon = ({ size = 24, className }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 3.4L3 21" />
        <path d="M9 10a0.5 .5 0 0 0 1 1a5 5 0 0 0 5 5a0.5 .5 0 0 0 1-1" />
    </svg>
);

// ─── FAQ DATA STRUCTURE ───
const FAQ_FLOW = {
    root: {
        text: "Hey! How can I help you today? Select a topic below:",
        options: [
            { label: "My Courses & Content", next: "courses" },
            { label: "Live Classes & Schedule", next: "live_classes" },
            { label: "Assignments & Grades", next: "assignments" },
            { label: "Technical Issues", next: "technical" },
            { label: "Certificates & Admin", next: "admin" }
        ]
    },
    // 1. Courses Category
    courses: {
        text: "What do you need help with regarding your courses?",
        options: [
            { label: "Where are recorded lectures?", next: "recordings_location" },
            { label: "Course materials missing?", next: "materials_missing" },
            { label: "Course validity/expiry?", next: "course_validity" },
            { label: "Back to Menu", next: "root" }
        ]
    },
    recordings_location: {
        text: "You can find recorded lectures by going to 'My Courses' > Selecting your course > Clicking the 'Recorded' tab. It may take 2-4 hours for a live session to appear there.",
        options: [{ label: "Video playback issue?", next: "video_playback" }, { label: "That helped, thanks!", next: "feedback_positive" }]
    },
    materials_missing: {
        text: "Resources are uploaded by instructors under the 'Content' tab. If a specific file is missing, the instructor might have not uploaded it yet.",
        options: [{ label: "Notify Instructor", next: "notify_instructor_dummy" }, { label: "Back to Courses", next: "courses" }]
    },
    course_validity: {
        text: "Your course access is valid until the end of your unparalleled batch duration plus a grace period of 6 months.",
        options: [{ label: "Back to Menu", next: "root" }]
    },

    // 2. Live Classes Category
    live_classes: {
        text: "Live Class queries:",
        options: [
            { label: "How to join a class?", next: "join_class" },
            { label: "I missed a live class", next: "missed_class" },
            { label: "Class schedule?", next: "schedule_info" },
            { label: "Back to Menu", next: "root" }
        ]
    },
    join_class: {
        text: "A 'Join' button appears on your dashboard and course page 10 minutes before the scheduled start time.",
        options: [{ label: "Button not visible?", next: "tech_browser" }, { label: "Back to Live Classes", next: "live_classes" }]
    },
    missed_class: {
        text: "Don't worry! All live sessions are recorded. You can watch the recording in the 'Recorded' section after a few hours.",
        options: [{ label: "Go to Recordings", next: "recordings_location" }, { label: "Back to Menu", next: "root" }]
    },
    schedule_info: {
        text: "Your upcoming schedule is visible on the Dashboard calendar. You can also view the full timeline inside your course details.",
        options: [{ label: "Sync with Google Calendar?", next: "feature_request" }, { label: "Back to Menu", next: "root" }]
    },

    // 3. Assignments Category
    assignments: {
        text: "Assignments & Grades:",
        options: [
            { label: "How to submit?", next: "submit_assignment" },
            { label: "Deadline extension?", next: "deadline_extension" },
            { label: "Where are my grades?", next: "view_grades" },
            { label: "Back to Menu", next: "root" }
        ]
    },
    submit_assignment: {
        text: "Navigate to the 'Assignments' tab in your course. Click on the active assignment and upload your file (PDF/Zip recommended).",
        options: [{ label: "Upload failed?", next: "tech_upload" }, { label: "Back to Assignments", next: "assignments" }]
    },
    deadline_extension: {
        text: "Strict deadlines are set by instructors. You would need to contact your instructor directly via the Chat feature to request an extension.",
        options: [{ label: "Go to Chats", next: "redirect_chat" }, { label: "Back to Menu", next: "root" }]
    },
    view_grades: {
        text: "Grades and feedback are published in the 'Assignments' tab once graded. You'll also receive a notification bell alert.",
        options: [{ label: "Back to Menu", next: "root" }]
    },

    // 4. Technical Category
    technical: {
        text: "Technical Support:",
        options: [
            { label: "I can't log in", next: "login_issue" },
            { label: "Video buffering/Error", next: "video_playback" },
            { label: "Update Profile/Phone", next: "update_profile" },
            { label: "Back to Menu", next: "root" }
        ]
    },
    login_issue: {
        text: "Ensure you are using your registered email. If you forgot your PIN, please contact support to reset it.",
        options: [{ label: "Back to Tech Support", next: "technical" }]
    },
    video_playback: {
        text: "Please check your internet connection. We recommend Chrome or Edge browser for the best experience. Clearing cache often resolves playback errors.",
        options: [{ label: "Back to Menu", next: "root" }]
    },
    update_profile: {
        text: "You can update your avatar and basic details in Settings. For email or phone number changes, admin approval is required.",
        options: [{ label: "Go to Settings", next: "redirect_settings" }, { label: "Back to Menu", next: "root" }]
    },
    tech_browser: {
        text: "Please try refreshing the page or checking if your browser blocks pop-ups. Ensure you are not using an incognito window which might block local storage.",
        options: [{ label: "Back to Live Classes", next: "live_classes" }]
    },
    tech_upload: {
        text: "Ensure your file is under the size limit (usually 10MB) and in a supported format (PDF, DOCX, ZIP).",
        options: [{ label: "Back to Assignments", next: "assignments" }]
    },

    // 5. Admin Category
    admin: {
        text: "Administrative Queries:",
        options: [
            { label: "When will I get certificate?", next: "certificate_timeline" },
            { label: "Fees & Payment", next: "fees_payment" },
            { label: "Back to Menu", next: "root" }
        ]
    },
    certificate_timeline: {
        text: "Certificates are generated automatically after you complete 100% of the course content and pass all assignments. It will appear in your Profile.",
        options: [{ label: "Back to Admin", next: "admin" }]
    },
    fees_payment: {
        text: "For fee receipts or payment status, please visit the 'Settings' > 'Billing' section (if enabled) or ask the admin directly.",
        options: [{ label: "Back to Admin", next: "admin" }]
    },

    // End/Action Nodes
    feedback_positive: {
        text: "Great! Happy to help. Is there anything else?",
        options: [{ label: "No, I'm good", next: "end_chat" }, { label: "Main Menu", next: "root" }]
    },

    feature_request: {
        text: "That's a great idea! I'll note that down for our product team.",
        options: [{ label: "Main Menu", next: "root" }]
    },
    notify_instructor_dummy: {
        text: "I've sent a notification ping to your instructor about the missing content.",
        options: [{ label: "Thanks", next: "root" }]
    },
    redirect_chat: {
        text: "Redirecting you to the Chats page...",
        action: "redirect_chat",
        options: [{ label: "Main Menu", next: "root" }]
    },
    redirect_settings: {
        text: "Redirecting you to Settings...",
        action: "redirect_settings",
        options: [{ label: "Main Menu", next: "root" }]
    },
    end_chat: {
        text: "Have a wonderful learning day! 👋",
        options: [{ label: "Start Over", next: "root" }]
    }
};

export default function Chatbot({ isOpen, onClose }) {
    // Initial state includes the social media message and the greeting
    const [messages, setMessages] = useState([
        {
            id: 1,
            text: (
                <div className="flex flex-col gap-2">
                    <span className="font-medium text-foreground">Connect with us on:</span>
                    <div className="flex gap-3 mt-1">
                        <a
                            href="https://www.instagram.com/riseinstitute.tech/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-all hover:scale-110 shadow-sm"
                        >
                            <Instagram size={18} />
                        </a>
                        <a
                            href="https://api.whatsapp.com/send?phone=917972131295"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-success text-success-foreground rounded-full hover:bg-success/90 transition-all hover:scale-110 shadow-sm"
                        >
                            <WhatsAppIcon size={18} />
                        </a>
                        <a
                            href="https://www.linkedin.com/company/rise-institute-mumbai/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-all hover:scale-110 shadow-sm"
                        >
                            <Linkedin size={18} />
                        </a>
                    </div>
                </div>
            ),
            sender: 'bot',
            timestamp: new Date()
        },
        {
            id: 2,
            text: FAQ_FLOW.root.text,
            sender: 'bot',
            timestamp: new Date(),
            options: FAQ_FLOW.root.options
        },
    ]);

    const [inputText, setInputText] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const scrollRef = useRef(null);

    // Auto-scroll to bottom logic
    const scrollToBottom = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    };

    // UseEffect to scroll whenever messages or typing state changes
    useEffect(() => {
        setTimeout(scrollToBottom, 100);
    }, [messages, isTyping, isOpen]);


    const handleSendMessage = (e) => {
        e.preventDefault();
        if (!inputText.trim()) return;

        const userMsg = {
            id: Date.now(),
            text: inputText,
            sender: 'user',
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMsg]);
        setInputText("");

        // Simulate default response for free-text input
        simulateBotResponse({
            text: "I'm currently trained to answer FAQs via the options below. For specific queries, please select a topic or contact support.",
            options: FAQ_FLOW.root.options
        });
    };

    const handleOptionClick = (option) => {
        // 1. Add User Selection Message
        const userMsg = {
            id: Date.now(),
            text: option.label,
            sender: 'user',
            timestamp: new Date()
        };
        setMessages(prev => [...prev, userMsg]);

        // 2. Lookup Next Bot Response
        const nextNodeKey = option.next;
        const nextNode = FAQ_FLOW[nextNodeKey];

        if (nextNode) {
            simulateBotResponse(nextNode);
        }
    };

    const simulateBotResponse = (node) => {
        setIsTyping(true);
        setTimeout(() => {
            setIsTyping(false);
            const botMsg = {
                id: Date.now() + 1,
                text: node.text,
                sender: 'bot',
                timestamp: new Date(),
                options: node.options
            };
            setMessages(prev => [...prev, botMsg]);

            // Handle Actions (e.g. Redirects) - Logic placeholder
            if (node.action) {
                console.log("Triggering action:", node.action);
                // In a real app, use useNavigate here
            }

        }, 1200); // 1.2s delay for natural "reading/typing" feel
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.95 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="fixed bottom-6 right-6 w-[380px] h-[600px] bg-popover rounded-2xl shadow-2xl overflow-hidden flex flex-col z-50 border border-border font-sans"
                >
                    {/* Header */}
                    <div className="h-16 bg-sidebar flex items-center justify-between px-5 shrink-0 shadow-md">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Avatar className="h-10 w-10 border-2 border-white/20 shadow-sm">
                                    <AvatarFallback className="bg-primary text-primary-foreground font-bold text-xs">AI</AvatarFallback>
                                </Avatar>
                                <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute bottom-0 right-0 w-3 h-3 bg-success border-2 border-sidebar-border rounded-full"
                                ></motion.span>
                            </div>
                            <div>
                                <h3 className="text-white font-semibold text-sm tracking-wide">Support Assistant</h3>
                                <span className="text-primary/70 text-[11px] font-medium flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-pulse"></span>
                                    Online
                                </span>
                            </div>
                        </div>
                        {/* CHANGED MINIMIZE ICON TO X ICON HERE */}
                        <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent rounded-full transition-colors" onClick={onClose}>
                                <X size={18} />
                            </Button>
                        </div>
                    </div>

                    {/* Messages Area - Using div with overflow for better ref control than ScrollArea sometimes */}
                    <div
                        className="flex-1 bg-muted/50 p-4 overflow-y-auto custom-scrollbar space-y-5"
                        ref={scrollRef}
                    >
                        {messages.map((msg, idx) => (
                            <motion.div
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.3 }}
                                key={msg.id}
                                className={cn(
                                    "flex flex-col w-full gap-1",
                                    msg.sender === 'user' ? "items-end" : "items-start"
                                )}
                            >
                                {/* Message Bubble */}
                                <div className={cn(
                                    "max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-sm relative group",
                                    msg.sender === 'user'
                                        ? "bg-primary text-primary-foreground rounded-br-none"
                                        : "bg-card text-card-foreground border border-border rounded-bl-none"
                                )}>
                                    {/* Render text or component */}
                                    <div className="leading-relaxed">
                                        {msg.text}
                                    </div>

                                    <div className={cn(
                                        "text-[10px] mt-1.5 font-medium opacity-70",
                                        msg.sender === 'user' ? "text-primary-foreground/80 text-right" : "text-muted-foreground text-left"
                                    )}>
                                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>

                                {/* Option Chips (Only for Bot) */}
                                {msg.sender === 'bot' && msg.options && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.2 }}
                                        className="flex flex-wrap gap-2 mt-1 max-w-[90%]"
                                    >
                                        {msg.options.map((opt, i) => (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                key={i}
                                                onClick={() => handleOptionClick(opt)}
                                                className="text-xs bg-card hover:bg-accent text-primary hover:text-primary-foreground border border-border hover:border-transparent px-3 py-1.5 rounded-full shadow-xs transition-all duration-200 flex items-center gap-1 group/btn"
                                            >
                                                {opt.label}
                                                <ChevronRight size={12} className="opacity-0 group-hover/btn:opacity-100 -ml-1 group-hover/btn:ml-0 transition-all" />
                                            </Button>
                                        ))}
                                    </motion.div>
                                )}
                            </motion.div>
                        ))}

                        {/* Typing Indicator */}
                        {isTyping && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex justify-start w-full"
                            >
                                <div className="bg-card border border-border px-4 py-3 rounded-2xl rounded-bl-none shadow-sm flex items-center gap-1 w-fit">
                                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce"></span>
                                </div>
                            </motion.div>
                        )}
                        <div className="h-2" /> {/* Spacer */}
                    </div>

                    {/* Input Area */}
                    <div className="p-4 bg-card border-t border-border">
                        <form onSubmit={handleSendMessage} className="relative flex items-center gap-2">
                            <Input
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                placeholder="Type a message..."
                                className="flex-1 h-11 rounded-xl bg-input border-input focus-visible:ring-2 focus-visible:ring-ring transition-all px-4 text-sm"
                            />

                            <Button
                                type="submit"
                                size="icon"
                                className={cn(
                                    "h-10 w-10 rounded-xl shadow-sm transition-all duration-200 shrink-0",
                                    inputText.trim()
                                        ? "bg-primary hover:bg-primary/90 text-primary-foreground translate-x-0 opacity-100"
                                        : "bg-muted text-muted-foreground translate-x-2 opacity-0 pointer-events-none"
                                )}
                                disabled={!inputText.trim()}
                            >
                                <Send size={18} />
                            </Button>
                        </form>
                        <div className="text-center mt-2">
                            <p className="text-[10px] text-muted-foreground">Powered by <BrandName /> AI • v1.2</p>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}